/**
 * EnhancedTimeSeriesChart - Modern time series visualization
 * Features: Gradient fills, threshold zones, anomaly highlighting, and better interactivity
 */

import Plot from 'react-plotly.js'
import { Data, Layout, Shape } from 'plotly.js'
import { useMemo } from 'react'
import clsx from 'clsx'

interface TimeSeriesData {
  x: string[] | Date[]
  y: number[]
  name: string
  color?: string
  type?: 'line' | 'scatter' | 'area'
  showMarkers?: boolean
}

interface ThresholdZone {
  min: number
  max: number
  color: string
  label: string
}

interface AnomalyPoint {
  x: string | Date
  y: number
  label?: string
}

interface EnhancedTimeSeriesChartProps {
  data: TimeSeriesData[]
  title?: string
  subtitle?: string
  xAxisTitle?: string
  yAxisTitle?: string
  height?: number
  showLegend?: boolean
  threshold?: number
  thresholdLabel?: string
  thresholdZones?: ThresholdZone[]
  anomalyPoints?: AnomalyPoint[]
  showGrid?: boolean
  showRangeSlider?: boolean
  className?: string
}

// Enhanced color palette
const COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#ec4899', // pink
]

export default function EnhancedTimeSeriesChart({
  data,
  title,
  subtitle,
  xAxisTitle,
  yAxisTitle,
  height = 320,
  showLegend = true,
  threshold,
  thresholdLabel = 'Threshold',
  thresholdZones,
  anomalyPoints,
  showGrid = true,
  showRangeSlider = false,
  className
}: EnhancedTimeSeriesChartProps) {
  const traces: Data[] = useMemo(() => {
    const result: Data[] = []

    // Add main data series
    data.forEach((series, index) => {
      const color = series.color || COLORS[index % COLORS.length]

      if (series.type === 'area') {
        // Area chart with gradient fill
        result.push({
          x: series.x,
          y: series.y,
          name: series.name,
          type: 'scatter',
          mode: series.showMarkers ? 'lines+markers' : 'lines',
          fill: 'tozeroy',
          fillcolor: `${color}20`,
          line: {
            color: color,
            width: 2,
            shape: 'spline'
          },
          marker: {
            color: color,
            size: 6
          },
          hovertemplate: `<b>${series.name}</b><br>%{x}<br>Value: %{y:.2f}<extra></extra>`
        })
      } else {
        // Line or scatter chart
        result.push({
          x: series.x,
          y: series.y,
          name: series.name,
          type: 'scatter',
          mode: series.type === 'scatter' ? 'markers' : (series.showMarkers ? 'lines+markers' : 'lines'),
          line: {
            color: color,
            width: 2.5,
            shape: 'spline'
          },
          marker: {
            color: color,
            size: 6,
            line: {
              color: '#1e293b',
              width: 1
            }
          },
          hovertemplate: `<b>${series.name}</b><br>%{x}<br>Value: %{y:.2f}<extra></extra>`
        })
      }
    })

    // Add threshold line - with comprehensive null safety
    if (threshold !== undefined && data.length > 0 && data[0]?.x?.length > 0) {
      result.push({
        x: [data[0].x[0], data[0].x[data[0].x.length - 1]],
        y: [threshold, threshold],
        name: thresholdLabel,
        type: 'scatter',
        mode: 'lines',
        line: {
          color: '#ef4444',
          width: 2,
          dash: 'dash'
        },
        hovertemplate: `<b>${thresholdLabel}</b><br>%{y:.2f}<extra></extra>`
      })
    }

    // Add anomaly points
    if (anomalyPoints && anomalyPoints.length > 0) {
      result.push({
        x: anomalyPoints.map(p => p.x),
        y: anomalyPoints.map(p => p.y),
        name: 'Anomalies',
        type: 'scatter',
        mode: 'markers',
        marker: {
          color: '#ef4444',
          size: 12,
          symbol: 'diamond',
          line: {
            color: '#ffffff',
            width: 2
          }
        },
        hovertemplate: anomalyPoints.map(p =>
          `<b>Anomaly${p.label ? `: ${p.label}` : ''}</b><br>%{x}<br>Value: %{y:.2f}<extra></extra>`
        )
      })
    }

    return result
  }, [data, threshold, thresholdLabel, anomalyPoints])

  // Create threshold zone shapes
  const shapes: Partial<Shape>[] = useMemo(() => {
    if (!thresholdZones) return []

    return thresholdZones.map(zone => ({
      type: 'rect' as const,
      xref: 'paper' as const,
      yref: 'y' as const,
      x0: 0,
      x1: 1,
      y0: zone.min,
      y1: zone.max,
      fillcolor: zone.color,
      opacity: 0.1,
      line: { width: 0 }
    }))
  }, [thresholdZones])

  const layout: Partial<Layout> = useMemo(() => ({
    title: title ? {
      text: subtitle ? `<b>${title}</b><br><span style="font-size:12px;color:#94a3b8">${subtitle}</span>` : `<b>${title}</b>`,
      font: { color: '#e2e8f0', size: 14 },
      x: 0,
      xanchor: 'left'
    } : undefined,
    height,
    margin: { t: title ? 60 : 30, r: 30, b: showRangeSlider ? 80 : 50, l: 60 },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: { color: '#94a3b8', family: 'Inter, sans-serif', size: 12 },
    xaxis: {
      title: xAxisTitle ? { text: xAxisTitle, font: { color: '#94a3b8', size: 12 } } : undefined,
      gridcolor: showGrid ? '#334155' : 'transparent',
      gridwidth: 1,
      linecolor: '#475569',
      tickfont: { size: 11, color: '#64748b' },
      showgrid: showGrid,
      zeroline: false,
      rangeslider: showRangeSlider ? { visible: true, bgcolor: '#1e293b' } : undefined,
      showspikes: true,
      spikecolor: '#3b82f6',
      spikethickness: 1,
      spikedash: 'dot',
      spikemode: 'across'
    },
    yaxis: {
      title: yAxisTitle ? { text: yAxisTitle, font: { color: '#94a3b8', size: 12 } } : undefined,
      gridcolor: showGrid ? '#334155' : 'transparent',
      gridwidth: 1,
      linecolor: '#475569',
      tickfont: { size: 11, color: '#64748b' },
      showgrid: showGrid,
      zeroline: false,
      showspikes: true,
      spikecolor: '#3b82f6',
      spikethickness: 1,
      spikedash: 'dot'
    },
    showlegend: showLegend,
    legend: {
      orientation: 'h',
      yanchor: 'bottom',
      y: 1.02,
      xanchor: 'left',
      x: 0,
      font: { size: 11, color: '#94a3b8' },
      bgcolor: 'transparent',
      itemsizing: 'constant'
    },
    hovermode: 'x unified',
    hoverlabel: {
      bgcolor: '#1e293b',
      bordercolor: '#475569',
      font: { color: '#e2e8f0', size: 12 }
    },
    shapes
  }), [title, subtitle, height, xAxisTitle, yAxisTitle, showGrid, showLegend, showRangeSlider, shapes])

  const config = {
    displayModeBar: true,
    modeBarButtonsToRemove: ['lasso2d', 'select2d', 'autoScale2d'] as any[],
    displaylogo: false,
    responsive: true,
    toImageButtonOptions: {
      format: 'png' as const,
      filename: title || 'chart',
      height: 600,
      width: 1000,
      scale: 2
    }
  }

  return (
    <div className={clsx('relative', className)}>
      {/* Zone legend (if zones exist) */}
      {thresholdZones && thresholdZones.length > 0 && (
        <div className="absolute top-0 right-0 flex items-center gap-3 text-xs z-10">
          {thresholdZones.map((zone, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <div
                className="w-3 h-3 rounded"
                style={{ backgroundColor: zone.color, opacity: 0.5 }}
              />
              <span className="text-surface-400">{zone.label}</span>
            </div>
          ))}
        </div>
      )}

      <Plot
        data={traces}
        layout={layout}
        config={config}
        style={{ width: '100%' }}
      />
    </div>
  )
}
